import { ExternalLink, Activity, Droplets, Sparkles } from "lucide-react";
import { IMG } from "@/data/catalog";
import { mediaUrl as M } from "@/data/pdpMedia";
import { SectionHeading } from "@/components/common/ui";

const STUDIES = [
  {
    icon: Activity,
    title: "Collagen & fine lines",
    body: "A randomised controlled trial of 136 people found red-light treatment significantly increased intradermal collagen density and reduced wrinkle depth by up to 36%.",
    cite: "Wunsch & Matuschka, Photomedicine and Laser Surgery, 2014",
    href: "https://pmc.ncbi.nlm.nih.gov/articles/PMC3926176/",
  },
  {
    icon: Droplets,
    title: "Breakouts & clarity",
    body: "Blue light (~415nm) has been shown to target C. acnes bacteria deep in the pores, reducing inflammatory acne lesions in mild-to-moderate cases.",
    cite: "Gold et al., clinical blue-light study, 2011",
    href: "https://pmc.ncbi.nlm.nih.gov/articles/PMC2923954/",
  },
  {
    icon: Sparkles,
    title: "Repair, tone & pigmentation",
    body: "A dermatology review confirms LED phototherapy is a safe, non-invasive way to support skin rejuvenation, pigmentation and repair across a broad range of concerns.",
    cite: "Ablon, J Clin Aesthet Dermatol, 2018",
    href: "https://pmc.ncbi.nlm.nih.gov/articles/PMC5843358/",
  },
];

export const ResearchBacked = () => (
  <section className="bg-[#232222] py-24 text-[#FFFCFA] md:py-32" data-testid="research-backed">
    <div className="mx-auto grid max-w-[1200px] items-center gap-12 px-6 md:px-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
      <div className="relative overflow-hidden rounded-[2.5rem] border border-white/10">
        <img src={M("pdp_research")} data-media-slot="pdp_research" data-media-type="image" alt="Mature woman with naturally radiant skin" className="aspect-[4/5] w-full object-cover" />
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-6">
          <p className="text-xs font-light text-white/80">LED light therapy is studied across all ages, and especially loved by women 40+.</p>
        </div>
      </div>
      <div>
        <p className="mb-4 text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-[#FBC4A8]">Backed by Science</p>
        <h2 className="font-display text-4xl font-light leading-[1.05] tracking-tight text-[#FFFCFA] sm:text-5xl">The research behind the <span className="text-[#FBC4A8]">glow.</span></h2>
        <p className="mt-5 max-w-xl text-base font-light leading-relaxed text-white/70 md:text-lg">LED light therapy isn't a trend, it's one of the most studied tools in modern dermatology. Here's a look at the published evidence.</p>
        <div className="mt-10 space-y-4">
          {STUDIES.map((s) => {
            const Icon = s.icon;
            return (
              <a key={s.title} href={s.href} target="_blank" rel="noopener noreferrer" data-testid={`study-${s.title.split(" ")[0].toLowerCase()}`}
                className="group flex gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-5 transition-colors hover:bg-white/[0.08]">
                <span className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-[#FBC4A8] text-[#232222]">
                  <Icon className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-display text-lg text-[#FFFCFA]">{s.title}</p>
                  <p className="mt-1.5 text-sm font-light leading-relaxed text-white/75">{s.body}</p>
                  <span className="mt-2.5 inline-flex items-center gap-1.5 text-xs text-[#FBC4A8]">
                    {s.cite} <ExternalLink className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </a>
            );
          })}
        </div>
        <p className="mt-6 text-xs font-light text-white/50">Studies refer to LED light therapy in general. Individual results vary, and this device is not intended to diagnose or treat any medical condition.</p>
      </div>
    </div>
  </section>
);
